import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import { useCallback, useEffect, useRef } from 'react';
import { apiPost } from '../api';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  label?: string;
}

// ── Toolbar button ─────────────────────────────────────────────

function ToolbarBtn({
  onClick,
  active,
  disabled,
  title,
  children,
}: {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        padding: '4px 8px',
        border: '1px solid #ccc',
        borderRadius: 4,
        background: active ? '#e0e0e0' : '#fff',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontWeight: active ? 700 : 400,
        fontSize: 13,
        lineHeight: 1.2,
        minWidth: 28,
      }}
    >
      {children}
    </button>
  );
}

// ── Image upload helper ────────────────────────────────────────

async function uploadImage(file: File): Promise<string | null> {
  try {
    const { stagedTarget } = await apiPost<{
      stagedTarget: {
        url: string;
        resourceUrl: string;
        parameters: Array<{ name: string; value: string }>;
      };
    }>('/panel-api/images/staged-upload', {
      fileName: file.name,
      mimeType: file.type || 'image/jpeg',
      fileSize: String(file.size),
    });

    const formData = new FormData();
    for (const param of stagedTarget.parameters) {
      formData.append(param.name, param.value);
    }
    formData.append('file', file);

    const uploadRes = await fetch(stagedTarget.url, {
      method: 'POST',
      body: formData,
    });

    if (!uploadRes.ok) return null;

    const { imageUrl } = await apiPost<{ fileId: string; imageUrl: string }>(
      '/panel-api/images/register',
      { resourceUrl: stagedTarget.resourceUrl, fileName: file.name },
    );

    return imageUrl;
  } catch {
    return null;
  }
}

// ── Component ──────────────────────────────────────────────────

export default function RichTextEditor({ value, onChange, label }: RichTextEditorProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Image.configure({
        inline: false,
        allowBase64: false,
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
      }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  // Sync external value changes (e.g. locale tab switch)
  const lastExternalValue = useRef(value);
  useEffect(() => {
    if (editor && value !== lastExternalValue.current) {
      lastExternalValue.current = value;
      const currentHtml = editor.getHTML();
      if (currentHtml !== value) {
        editor.commands.setContent(value || '');
      }
    }
  }, [value, editor]);

  const addImage = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !editor) return;
      e.target.value = ''; // Reset for next upload

      const url = await uploadImage(file);
      if (url) {
        editor.chain().focus().setImage({ src: url }).run();
      }
    },
    [editor],
  );

  const addLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL del enlace:', previousUrl || 'https://');

    if (url === null) return; // Cancelled
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  }, [editor]);

  if (!editor) return null;

  return (
    <div>
      {label && (
        <div style={{ marginBottom: 4, fontSize: 13, fontWeight: 600 }}>{label}</div>
      )}

      {/* Toolbar */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 4,
          padding: '6px 8px',
          border: '1px solid #ccc',
          borderBottom: 'none',
          borderRadius: '8px 8px 0 0',
          background: '#f9f9f9',
        }}
      >
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive('bold')}
          title="Negrita"
        >
          <strong>B</strong>
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive('italic')}
          title="Cursiva"
        >
          <em>I</em>
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleStrike().run()}
          active={editor.isActive('strike')}
          title="Tachado"
        >
          <s>S</s>
        </ToolbarBtn>

        <div style={{ width: 1, background: '#ddd', margin: '0 2px' }} />

        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          active={editor.isActive('heading', { level: 1 })}
          title="Título 1"
        >
          H1
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          active={editor.isActive('heading', { level: 2 })}
          title="Título 2"
        >
          H2
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          active={editor.isActive('heading', { level: 3 })}
          title="Título 3"
        >
          H3
        </ToolbarBtn>

        <div style={{ width: 1, background: '#ddd', margin: '0 2px' }} />

        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive('bulletList')}
          title="Lista"
        >
          • Lista
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive('orderedList')}
          title="Lista numerada"
        >
          1. Lista
        </ToolbarBtn>

        <div style={{ width: 1, background: '#ddd', margin: '0 2px' }} />

        <ToolbarBtn onClick={addLink} active={editor.isActive('link')} title="Enlace">
          🔗
        </ToolbarBtn>
        <ToolbarBtn onClick={addImage} title="Insertar imagen">
          🖼️
        </ToolbarBtn>

        <div style={{ width: 1, background: '#ddd', margin: '0 2px' }} />

        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          active={editor.isActive('blockquote')}
          title="Cita"
        >
          " Cita
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          title="Separador"
        >
          ―
        </ToolbarBtn>
      </div>

      {/* Editor */}
      <div
        style={{
          border: '1px solid #ccc',
          borderRadius: '0 0 8px 8px',
          minHeight: 200,
          padding: 12,
          background: '#fff',
        }}
      >
        <style>{`
          .ProseMirror { outline: none; min-height: 180px; }
          .ProseMirror h1 { font-size: 1.6em; font-weight: 700; margin: 0.6em 0 0.3em; }
          .ProseMirror h2 { font-size: 1.3em; font-weight: 600; margin: 0.5em 0 0.25em; }
          .ProseMirror h3 { font-size: 1.1em; font-weight: 600; margin: 0.4em 0 0.2em; }
          .ProseMirror p { margin: 0.4em 0; line-height: 1.6; }
          .ProseMirror ul, .ProseMirror ol { padding-left: 1.5em; margin: 0.4em 0; }
          .ProseMirror li { margin: 0.2em 0; }
          .ProseMirror blockquote { border-left: 3px solid #ddd; padding-left: 12px; color: #666; margin: 0.5em 0; }
          .ProseMirror img { max-width: 100%; height: auto; border-radius: 8px; margin: 0.5em 0; }
          .ProseMirror a { color: #2563eb; text-decoration: underline; }
          .ProseMirror hr { border: none; border-top: 1px solid #ddd; margin: 1em 0; }
        `}</style>
        <EditorContent editor={editor} />
      </div>

      {/* Hidden file input for image upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />
    </div>
  );
}
